import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { activateNativeDesign } from '@/lib/themeActivation';

interface MerchantRow {
  id: number;
  user_name: string | null;
  nomcommerce: string | null;
  name: string | null;
  image: string | null;
  theme: string | null;
}

interface ActiveRow {
  idcommerce: number | null;
  nomtheme: string | null;
  status: string | null;
}

interface Props {
  onClose: () => void;
}

/**
 * Activation du design natif Zifek pour un commerce.
 * Le design natif = aucun thème : le site public utilise la vitrine intégrée
 * et les couleurs de la charte graphique du commerce.
 */
export default function NativeDesignModal({ onClose }: Props) {
  const [merchants, setMerchants] = useState<MerchantRow[]>([]);
  const [activeRows, setActiveRows] = useState<Record<number, ActiveRow>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [notice, setNotice] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [usersRes, activeRes] = await Promise.all([
        supabase
          .from('users')
          .select('id, user_name, nomcommerce, name, image, theme')
          .not('user_name', 'is', null)
          .order('id', { ascending: false })
          .limit(300),
        supabase.from('sitewebthemeactuelle').select('idcommerce, nomtheme, status'),
      ]);
      if (usersRes.error) throw usersRes.error;
      setMerchants((usersRes.data || []) as MerchantRow[]);
      const map: Record<number, ActiveRow> = {};
      (activeRes.data || []).forEach((row: ActiveRow) => {
        if (row.idcommerce != null) map[Number(row.idcommerce)] = row;
      });
      setActiveRows(map);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de charger les commerces.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return merchants;
    return merchants.filter((m) =>
      `${m.nomcommerce || ''} ${m.name || ''} ${m.user_name || ''}`.toLowerCase().includes(q),
    );
  }, [merchants, query]);

  const applyNative = async (merchant: MerchantRow) => {
    setBusyId(merchant.id);
    setNotice('');
    try {
      await activateNativeDesign(merchant.id);
      setActiveRows((prev) => ({
        ...prev,
        [merchant.id]: { idcommerce: merchant.id, nomtheme: 'Design natif Zifek', status: 'native' },
      }));
      setNotice(`« ${merchant.nomcommerce || merchant.name || merchant.user_name} » utilise maintenant le design natif.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de l'activation.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-[70]" onClick={onClose} />
      <div className="fixed inset-0 z-[71] flex items-center justify-center p-4">
        <div className="w-full max-w-2xl max-h-[88vh] bg-background-50 border border-background-200/70 rounded-lg flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70 flex-shrink-0">
            <span className="text-sm font-semibold text-foreground-900 flex items-center gap-2">
              <i className="ri-smartphone-line text-accent-500"></i>
              Activer le design natif
            </span>
            <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 cursor-pointer">
              <i className="ri-close-line text-foreground-500"></i>
            </button>
          </div>

          <div className="px-5 py-3 border-b border-background-200/70 flex-shrink-0">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher un commerce (nom, sous-domaine)…"
                className="w-full h-10 pl-9 pr-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
              />
            </div>
            <p className="text-[11px] text-foreground-400 mt-2">
              Le design natif supprime le thème : la vitrine intégrée Zifek s&apos;affiche avec les couleurs de la charte du commerce.
            </p>
            {notice && (
              <p className="mt-2 text-[11px] text-accent-700 bg-accent-50 border border-accent-100 rounded-md px-2.5 py-1.5 flex items-center gap-1.5">
                <i className="ri-check-line"></i>{notice}
              </p>
            )}
            {error && (
              <p className="mt-2 text-[11px] text-red-600 bg-red-50 border border-red-100 rounded-md px-2.5 py-1.5 flex items-center gap-1.5">
                <i className="ri-error-warning-line"></i>{error}
              </p>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-1.5">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i>
              </div>
            ) : filtered.length === 0 ? (
              <p className="text-center text-sm text-foreground-500 py-10">Aucun commerce trouvé.</p>
            ) : (
              filtered.map((m) => {
                const current = activeRows[m.id];
                const isNative = current?.status === 'native';
                return (
                  <div key={m.id} className="flex items-center gap-3 px-3 py-2.5 rounded-md border border-background-200/70 bg-background-50">
                    <div className="w-9 h-9 rounded-md bg-background-100 overflow-hidden flex items-center justify-center flex-shrink-0">
                      {m.image ? (
                        <img src={m.image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <i className="ri-store-2-line text-foreground-400"></i>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground-900 truncate">
                        {m.nomcommerce || m.name || m.user_name}
                      </p>
                      <p className="text-[11px] text-foreground-400 truncate">
                        {m.user_name ? `${m.user_name} · ` : ''}
                        {isNative ? 'Design natif' : current?.nomtheme ? `Thème : ${current.nomtheme}` : 'Aucun thème'}
                      </p>
                    </div>
                    {isNative ? (
                      <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-accent-100 text-accent-700 whitespace-nowrap flex items-center gap-1">
                        <i className="ri-check-line"></i>Natif
                      </span>
                    ) : (
                      <button
                        onClick={() => void applyNative(m)}
                        disabled={busyId === m.id}
                        className="px-3 py-1.5 rounded-full text-[11px] font-semibold bg-foreground-950 text-background-50 hover:bg-foreground-800 disabled:opacity-50 cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                      >
                        {busyId === m.id ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-smartphone-line"></i>}
                        Activer
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </>
  );
}